<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Transcript -->
    <div
      ref="scroller"
      class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-6"
      @scroll.passive="onScroll">
      <NtLoader :loading="loading" debounce>
        <div class="w-full max-w-192 mx-auto">
          <NtSpacing>
            <NtSpacing class="text-center" compact>
              <img :src="assistant.icon_url" class="w-36 h-36 rounded-full mx-auto" />

              <NtHeading :level="2">{{ assistant.name }}</NtHeading>
              <p v-if="!conversationId && assistant.greeting">{{ assistant.greeting }}</p>
            </NtSpacing>

            <NtSpacing v-if="conversation.messages.length > 0">
              <NtAiAssistantMessage v-for="(message, i) in conversation.messages" :key="i" :message="message" />
              <i v-if="thinking" class="fass fa-circle animate-chat-pulse"></i>
            </NtSpacing>

            <p v-if="error" class="text-red-600">
              <i class="fass fa-exclamation-triangle"></i> Sorry, I was unable to respond. Please try again later.
            </p>
          </NtSpacing>
        </div>
      </NtLoader>
    </div>

    <!-- Composer, pinned to the bottom -->
    <div
      class="shrink-0 backdrop-blur-md bg-neutral-100/30 dark:bg-neutral-900/30 border-t border-neutral-900/10 dark:border-neutral-100/10 px-4 sm:px-6 py-3">
      <div class="w-full max-w-192 mx-auto flex flex-wrap gap-3 items-end">
        <NtInput
          v-model="newMessage"
          placeholder="Ask anything... (Shift+Enter for new line)"
          :rows="2"
          :max-rows="6"
          auto-grow
          class="flex-1"
          @keydown="onKeydown"
          no-debounce />

        <NtButton
          @click="eventSource ? stopStream() : sendMessage()"
          level="primary"
          class="w-10 h-10 rounded-full!"
          :icon="eventSource ? 'fa-stop' : 'fa-arrow-right'"
          :disabled="!newMessage && !eventSource" />
      </div>
    </div>
  </div>
</template>

<script>
import NtInput from '@/components/forms/NtInput.vue'
import NtButton from '@/components/general/NtButton.vue'
import NtHeading from '@/components/general/NtHeading.vue'
import NtLoader from '@/components/general/NtLoader.vue'
import NtSpacing from '@/components/general/NtSpacing.vue'
import NtAiAssistantMessage from './NtAiAssistantMessage.vue'
import { useSystemStore } from '@/stores/system'
import { useAuthStore } from '@/stores/auth'
import { mapState } from 'pinia'

// How long the stream may go silent before we give up on it. The clock restarts on every event,
// so this is the gap between events rather than the length of the whole response.
const STREAM_IDLE_TIMEOUT = 60000

export default {
  components: {
    NtInput,
    NtLoader,
    NtHeading,
    NtSpacing,
    NtButton,
    NtAiAssistantMessage
  },
  props: {
    conversationId: {
      type: String,
      default: null
    }
  },
  emits: ['conversation-created', 'conversation-unavailable'],
  data() {
    return {
      loading: true,
      assistant: null,
      conversation: {
        id: this.conversationId,
        messages: []
      },
      eventSource: null,
      newMessage: null,
      thinking: false,
      error: false,
      // Auto scrolling only follows the stream while the member is already at the bottom,
      // so reading back through the transcript is never interrupted.
      isPinnedToBottom: true
    }
  },
  computed: {
    ...mapState(useSystemStore, ['hub']),
    ...mapState(useAuthStore, ['selectedOffice']),
    assistantId() {
      return this.hub?.ai_assistant_id
    },
    readonly() {
      return (
        this.conversationId &&
        this.conversation.messages.length > 0 &&
        this.conversation.messages[this.conversation.messages.length - 1]?.type === 'output' &&
        !this.thinking &&
        !this.eventSource
      )
    }
  },
  async created() {
    await this.getAssistant()
    if (this.conversationId) {
      await this.getConversation()
    }
    this.loading = false

    // Open an existing conversation on its most recent message.
    this.scrollToBottom(true)
  },
  beforeUnmount() {
    clearTimeout(this.streamTimeout)
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
  },
  methods: {
    onScroll() {
      const el = this.$refs.scroller

      if (!el) {
        return
      }

      // A small threshold keeps us "pinned" through sub-pixel rounding and slight overscroll.
      this.isPinnedToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    },
    scrollToBottom(force = false) {
      if (!force && !this.isPinnedToBottom) {
        return
      }

      if (force) {
        this.isPinnedToBottom = true
      }

      this.$nextTick(() => {
        const el = this.$refs.scroller

        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },
    onKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        if (!this.eventSource) {
          this.sendMessage()
        }
      }
    },
    async getAssistant() {
      const response = await this.$api.get(`v3/ai-assistants/${this.assistantId}`)
      this.assistant = response.data
    },
    async getConversation() {
      try {
        const response = await this.$api.get(
          `v3/ai-assistants/${this.assistantId}/conversations/${this.conversationId}`
        )
        this.conversation = response.data
        this.conversation.id = this.conversationId
      } catch (err) {
        // The conversation has been pruned or is no longer ours, so fall back to a new one.
        console.error('Failed to load conversation:', err)
        this.conversation = { id: null, messages: [] }
        this.$emit('conversation-unavailable')
      }
    },
    async createConversation() {
      const response = await this.$api.post(`v3/ai-assistants/${this.assistantId}/conversations`, {
        office_id: this.selectedOffice?.id || null
      })
      this.conversation.id = response.data.id
      this.$emit('conversation-created', this.conversation.id)
    },
    stopStream() {
      clearTimeout(this.streamTimeout)
      this.eventSource?.close()
      this.eventSource = null
      this.thinking = false
    },
    async sendMessage() {
      if (!this.newMessage || this.eventSource) {
        return
      }

      if (!this.conversation.id) {
        await this.createConversation()
      }

      const response = await this.$api.post(
        `v3/ai-assistants/${this.assistantId}/conversations/${this.conversation.id}/messages`,
        { message: this.newMessage }
      )
      const userMsg = response.data
      this.conversation.messages.push(userMsg)
      this.newMessage = null

      // The member just sent this, so always bring it into view.
      this.scrollToBottom(true)

      this.thinking = true
      this.error = false
      let assistantMessage = null
      const outputIndexMap = {} // Maps OpenAI output_index to our content array index

      try {
        this.eventSource?.close()
        clearTimeout(this.streamTimeout)

        const params = new URLSearchParams({
          access_token: localStorage.accessToken,
          ...(this.selectedOffice?.id && { office_id: this.selectedOffice.id })
        })
        this.eventSource = new EventSource(
          `${import.meta.env.VITE_API_HOST}/v3/ai-assistants/${this.assistantId}/conversations/${this.conversation.id}/messages/${response.data.id}/response?${params}`
        )

        // Give up only once the stream has actually gone quiet. Timing the whole response from its
        // first word abandoned replies that were still coming: a response opens with the MCP
        // handshake and any tool calls, which produce no text at all, and closing the connection
        // early also stops the server ever recording the reply it was midway through receiving.
        const watchStream = () => {
          clearTimeout(this.streamTimeout)
          this.streamTimeout = setTimeout(() => {
            if (!this.eventSource) {
              return
            }

            console.error('Stream timeout — no activity received')
            this.eventSource.close()
            this.eventSource = null
            this.error = true
            this.thinking = false
            if (assistantMessage && assistantMessage.content.length === 0) {
              const idx = this.conversation.messages.indexOf(assistantMessage)
              if (idx > -1) this.conversation.messages.splice(idx, 1)
            }
          }, STREAM_IDLE_TIMEOUT)
        }

        watchStream()

        this.eventSource.onmessage = (event) => {
          try {
            // Any event at all means the stream is alive, including the tool calls and the
            // handshake that precede the first word of the answer.
            watchStream()

            const data = JSON.parse(event.data)
            const msgIndex = this.conversation.messages.indexOf(assistantMessage)

            if (data.type === 'response.created') {
              assistantMessage = {
                type: 'output',
                content: [],
                tokens_used: null
              }
              this.conversation.messages.push(assistantMessage)
            } else if (data.type === 'response.output_item.added') {
              const arrIdx = this.conversation.messages[msgIndex].content.push(data.item) - 1
              outputIndexMap[data.output_index] = arrIdx
            } else if (data.type === 'response.content_part.added') {
              const arrIdx = outputIndexMap[data.output_index]
              if (arrIdx !== undefined) {
                this.conversation.messages[msgIndex].content[arrIdx].content.push(data.part)
              }
            } else if (data.type === 'response.output_text.delta') {
              this.thinking = false
              const arrIdx = outputIndexMap[data.output_index]
              if (arrIdx !== undefined) {
                this.conversation.messages[msgIndex].content[arrIdx].content[data.content_index].text += data.delta
              }
            } else if (data.type === 'response.output_item.done') {
              const arrIdx = outputIndexMap[data.output_index]
              if (arrIdx !== undefined) {
                this.conversation.messages[msgIndex].content[arrIdx] = data.item
              }
            } else if (data.type === 'response.completed') {
              clearTimeout(this.streamTimeout)
              this.thinking = false
              this.eventSource?.close()
              this.eventSource = null

              // Check if the response actually has text content
              const hasContent = assistantMessage?.content?.some(
                (c) => c.type === 'message' && c.content?.some((p) => p.text?.trim())
              )

              if (!hasContent) {
                this.error = true
                if (assistantMessage) {
                  const idx = this.conversation.messages.indexOf(assistantMessage)
                  if (idx > -1) this.conversation.messages.splice(idx, 1)
                }
              } else if (data.response?.usage) {
                this.conversation.messages[this.conversation.messages.indexOf(userMsg)].tokens_used =
                  data.response.usage.input_tokens
                this.conversation.messages[msgIndex].tokens_used = data.response.usage.output_tokens
              }
            } else if (data.type === 'response.failed' || data.type === 'error') {
              clearTimeout(this.streamTimeout)
              console.error('Stream error:', data)
              this.eventSource?.close()
              this.eventSource = null
              this.error = true
              this.thinking = false
              if (assistantMessage && assistantMessage.content.length === 0) {
                const idx = this.conversation.messages.indexOf(assistantMessage)
                if (idx > -1) this.conversation.messages.splice(idx, 1)
              }
            }

            // Follow the response as it streams in, unless the member has scrolled away.
            this.scrollToBottom()
          } catch (err) {
            console.error('Failed to parse SSE data:', err)
            this.error = true
            this.thinking = false
          }
        }

        this.eventSource.onerror = (err) => {
          console.error('Connection error:', err)
          clearTimeout(this.streamTimeout)
          this.eventSource?.close()
          this.eventSource = null
          this.error = true
          this.thinking = false
          if (assistantMessage && assistantMessage.content.length === 0) {
            const idx = this.conversation.messages.indexOf(assistantMessage)
            if (idx > -1) this.conversation.messages.splice(idx, 1)
          }
        }
      } catch (err) {
        console.error('Failed to establish SSE connection:', err)
        this.error = true
        this.thinking = false
      }
    }
  },
  watch: {
    // Keep the thinking indicator and the error message in view as they appear.
    thinking() {
      this.scrollToBottom()
    },
    error(val) {
      if (val) {
        this.scrollToBottom()
      }
    }
  }
}
</script>
